/**
 * Endereço a partir de coordenada (Nominatim, público e sem chave).
 *
 * Existe porque o `reverseGeocodeAsync` do `expo-location` **não funciona na
 * web** — o serviço de geocodificação saiu do SDK 49 e o console avisa para
 * usar a API paga do Google. Sem isto, "estou aqui agora" pegava a
 * localização, não preenchia nada e parecia quebrado.
 *
 * É a mesma fonte que o mapa do onboarding usa, então o endereço que o pino
 * mostra e o que o botão preenche são o mesmo endereço.
 */
export interface ReverseAddress {
  cep: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  /** Sigla do estado ("MG"), que é como o endereço de uma linha é escrito. */
  state: string;
}

interface NominatimAddress {
  road?: string;
  pedestrian?: string;
  house_number?: string;
  suburb?: string;
  neighbourhood?: string;
  city_district?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  state?: string;
  postcode?: string;
  'ISO3166-2-lvl4'?: string;
}

/** "BR-MG" -> "MG". Sem isso o endereço sairia "Minas Gerais" por extenso. */
function uf(address: NominatimAddress): string {
  const iso = address['ISO3166-2-lvl4'];
  if (iso?.includes('-')) return iso.split('-')[1];
  return address.state ?? '';
}

export async function reverseGeocode(
  latitude: number,
  longitude: number,
): Promise<ReverseAddress | null> {
  const url =
    'https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1' +
    `&lat=${latitude}&lon=${longitude}`;

  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;

  const data = (await response.json()) as { address?: NominatimAddress };
  const address = data.address;
  if (!address) return null;

  return {
    cep: address.postcode ?? '',
    street: address.road ?? address.pedestrian ?? '',
    number: address.house_number ?? '',
    neighborhood:
      address.suburb ?? address.neighbourhood ?? address.city_district ?? '',
    city:
      address.city ?? address.town ?? address.village ?? address.municipality ?? '',
    state: uf(address),
  };
}

/**
 * Coordenada a partir do endereço em texto (Nominatim search).
 *
 * Usado para medir a distância até o prestador quando ele tem o endereço
 * escrito mas nunca fixou um pino no mapa. Mesma fonte do reverse, então bate
 * com o resto do app. Devolve `null` se o endereço não for reconhecido.
 *
 * O endereço salvo vem sujo ("Rua X, 321, Bairro, Cidade - UF, CEP 00000-000")
 * e a busca crua do Nominatim devolve vazio com esse tanto de ruído. Por isso
 * tenta uma cascata do mais específico ao mais grosso (rua+cidade, cidade+UF) e
 * para no primeiro acerto — melhor uma distância aproximada que nenhuma.
 */
export async function forwardGeocode(
  query: string,
): Promise<{ latitude: number; longitude: number } | null> {
  for (const term of buildGeocodeQueries(query)) {
    const hit = await geocodeOnce(term);
    if (hit) return hit;
  }
  return null;
}

async function geocodeOnce(
  term: string,
): Promise<{ latitude: number; longitude: number } | null> {
  const url =
    'https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=br' +
    `&q=${encodeURIComponent(term)}`;

  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;

  const data = (await response.json()) as { lat?: string; lon?: string }[];
  const hit = data?.[0];
  if (!hit?.lat || !hit?.lon) return null;

  const latitude = Number(hit.lat);
  const longitude = Number(hit.lon);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { latitude, longitude };
}

/** Do endereço sujo, gera tentativas do específico ao grosso, sem repetir. */
function buildGeocodeQueries(raw: string): string[] {
  const cleaned = raw
    .replace(/CEP\s*[\d.\s-]+/gi, '') // remove "CEP 00000-000"
    .replace(/\b\d{5}-?\d{3}\b/g, '') // e qualquer CEP solto
    .replace(/\s*-\s*/g, ', '); // "Cidade - UF" vira "Cidade, UF"

  const parts = cleaned
    .split(',')
    .map(p => p.trim())
    .filter(Boolean)
    .filter(p => !/^\d+$/.test(p)); // tira o número da casa

  const ufIndex = parts.findIndex(p => /^[A-Za-z]{2}$/.test(p));
  const uf = ufIndex >= 0 ? parts[ufIndex] : '';
  const street = parts[0] ?? '';
  const city =
    ufIndex > 0 ? parts[ufIndex - 1] : (parts[parts.length - 1] ?? '');

  const attempts = [
    parts.join(', '),
    [street, city, uf].filter(Boolean).join(', '),
    [street, city].filter(Boolean).join(', '),
    [city, uf].filter(Boolean).join(', '),
  ];

  return [...new Set(attempts.map(a => a.trim()).filter(Boolean))];
}
